import { BASEURL } from '../Constants';

export const CREDIT_REFRESH_EVENT = 'credits:refresh';
export const AUTH_TOKEN_CHANGED_EVENT = 'auth-token-changed';
export const CREDIT_CHECKOUT_CONTEXT_KEY = 'credit_checkout_context';

const getToken = () => sessionStorage.getItem('token');

const request = async (path, { method = 'GET', body, signal } = {}) => {
  const token = getToken();
  if (!token) {
    const error = new Error('Authentication is required.');
    error.code = 'AUTH_REQUIRED';
    throw error;
  }

  const response = await fetch(`${BASEURL}${path}`, {
    method,
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {})
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {})
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.error === 1 || data?.error === true) {
    const error = new Error(data?.message || `Credit request failed (${response.status}).`);
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
};

export const getCreditBalance = async (options = {}) => {
  const data = await request('/credits/balance', options);
  return data?.balance || data;
};

export const getCreditPricing = async (options = {}) => {
  const data = await request('/credits/pricing', options);
  return data;
};

export const getCreditTransactions = async ({ limit = 50, offset = 0, signal } = {}) => {
  const data = await request(`/credits/transactions?limit=${limit}&offset=${offset}`, { signal });
  return {
    transactions: Array.isArray(data?.transactions) ? data.transactions : [],
    total: Number(data?.total ?? data?.count ?? 0),
    limit: Number(data?.limit ?? limit),
    offset: Number(data?.offset ?? offset)
  };
};

export const createSubscriptionCheckout = (priceId) =>
  request('/credits/subscription/checkout', {
    method: 'POST',
    body: { price_id: priceId }
  });

export const createCreditPurchaseCheckout = (packageId) =>
  request('/credits/purchase/checkout', {
    method: 'POST',
    body: { package_id: packageId }
  });

export const createSubscriptionPortal = () =>
  request('/credits/subscription/portal', { method: 'POST' });

export const previewSubscriptionChange = async (priceId) => {
  const data = await request('/credits/subscription/change/preview', {
    method: 'POST',
    body: { price_id: priceId }
  });
  return data?.preview || data;
};

export const scheduleSubscriptionChange = (priceId) =>
  request('/credits/subscription/change', {
    method: 'POST',
    body: { price_id: priceId }
  });

export const removeScheduledSubscriptionChange = () =>
  request('/credits/subscription/change', { method: 'DELETE' });

export const cancelSubscription = () =>
  request('/credits/subscription/cancel', { method: 'POST' });

export const resumeSubscription = () =>
  request('/credits/subscription/resume', { method: 'POST' });

export const notifyCreditsChanged = (detail = {}) => {
  window.dispatchEvent(new CustomEvent(CREDIT_REFRESH_EVENT, { detail }));
};

export const saveCheckoutContext = (context) => {
  sessionStorage.setItem(CREDIT_CHECKOUT_CONTEXT_KEY, JSON.stringify({
    ...context,
    created_at: new Date().toISOString()
  }));
};

export const readCheckoutContext = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CREDIT_CHECKOUT_CONTEXT_KEY) || 'null');
  } catch {
    return null;
  }
};

export const clearCheckoutContext = () => {
  sessionStorage.removeItem(CREDIT_CHECKOUT_CONTEXT_KEY);
};
